import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AdminRequestError,
  fetchDialerSettings,
  restampRungs,
  updateRedialPolicy,
  type DialerSettingsResponse,
  type RedialPolicy,
} from './api';

// Its own settings entry rather than a section inside Dial admission. Dial admission is
// read-only and about what is STOPPING pools; this decides how often a lead is rung, which
// is the one thing on that screen an operator changes on purpose.
export function RedialLadderModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<DialerSettingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;

    fetchDialerSettings()
      .then((r) => live && setData(r))
      .catch((e: unknown) => {
        if (!live) return;
        setError(e instanceof AdminRequestError ? e.message : 'Could not load the redial ladder');
      });

    return () => {
      live = false;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Redial ladder"
        className="bg-card max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg border p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Redial ladder</h2>
            <p className="text-muted-foreground text-sm">
              How long a lead rests after each ring, and how many times it may ever be rung.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>

        {error && <p className="text-destructive mt-4 text-sm">{error}</p>}
        {!data && !error && <p className="text-muted-foreground mt-4 text-sm">Loading…</p>}

        {data && (
          <RedialLadderEditor
            key={data.settings.updated_at}
            saved={data.settings}
            redial={data.redial}
            onSaved={(next) => setData({ ...data, settings: next.settings, redial: next.redial })}
          />
        )}
      </div>
    </div>
  );
}

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};

// Hours in the inputs, seconds on the wire: the ladder is read in hours by everyone who
// talks about it, and the server keeps the floor (an hour after the first ring).
const toHours = (seconds: number) => Math.round((seconds / 3600) * 100) / 100;
const toSeconds = (hours: number) => Math.round(hours * 3600);

const numberOrNull = (raw: string): number | null => {
  const n = Number(raw);
  return raw.trim() === '' || Number.isNaN(n) ? null : n;
};

// The one editable thing in this panel. The ladder cannot stop the fleet — it only
// decides how long a lead rests between rings — so it is safe to move from here, and it
// is the knob most likely to be tuned by hand and measured between edits.
function RedialLadderEditor({
  saved,
  redial,
  onSaved,
}: {
  saved: DialerSettingsResponse['settings'];
  redial: RedialPolicy;
  onSaved: (next: { settings: DialerSettingsResponse['settings']; redial: RedialPolicy }) => void;
}) {
  // Seeded from the PERSISTED row, not the node-local "in force" value: on a clustered
  // fleet the row can be ahead of the cache this request happened to be served from, and
  // an editor seeded from the cache would save back values the operator never chose.
  const baseline = saved.ring_ladder ?? redial.deploy_ring_ladder;
  const [rungs, setRungs] = useState<string[]>(baseline.map((s) => String(toHours(s))));
  // Set only by touching a rung input or the +/- buttons. Hours are rounded for display,
  // so re-deriving seconds from the boxes would read an untouched ladder as changed.
  const [ladderDirty, setLadderDirty] = useState(false);
  const [lifetimeCap, setLifetimeCap] = useState(
    saved.max_dials_per_lead === null ? '' : String(saved.max_dials_per_lead),
  );
  const [releaseCap, setReleaseCap] = useState(
    saved.ring_release_max_rings === null ? '' : String(saved.ring_release_max_rings),
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const usingDefault = saved.ring_ladder === null;

  function run(action: () => Promise<string>) {
    setBusy(true);
    setError(null);
    setMessage(null);
    action()
      .then(setMessage)
      .catch((e: unknown) => setError(e instanceof AdminRequestError ? e.message : 'Save failed'))
      .finally(() => setBusy(false));
  }

  // Every write goes through this: the row must still be the one this editor loaded, or
  // two people with the panel open overwrite each other's untouched values and log a
  // treatment boundary nobody chose.
  function withFreshRow<T>(write: () => Promise<T>): Promise<T> {
    return fetchDialerSettings().then((live) => {
      if (live.settings.updated_at !== saved.updated_at) {
        onSaved({ settings: live.settings, redial: live.redial });
        throw new AdminRequestError(
          409,
          'Someone changed these settings since you opened the panel. It has been reloaded; check the values and save again.',
        );
      }
      return write();
    });
  }

  // Only the fields this operator changed go on the wire. The ladder is compared with the
  // one IN FORCE, not the stored one: on the deploy default the stored value is null while
  // the boxes show the deploy ladder, and sending that back would store a copy of it —
  // an unchosen ladder treatment that also stops later deploy-default changes applying.
  function save() {
    const patch: Parameters<typeof updateRedialPolicy>[0] = {};
    if (ladderDirty) patch.ring_ladder = rungs.map((r) => toSeconds(Number(r)));
    if (numberOrNull(lifetimeCap) !== saved.max_dials_per_lead) {
      patch.max_dials_per_lead = numberOrNull(lifetimeCap);
    }
    if (numberOrNull(releaseCap) !== saved.ring_release_max_rings) {
      patch.ring_release_max_rings = numberOrNull(releaseCap);
    }
    if (Object.keys(patch).length === 0) {
      setMessage('Nothing changed.');
      return;
    }

    run(() =>
      withFreshRow(() => updateRedialPolicy(patch)).then((r) => {
        setLadderDirty(false);
        onSaved(r);
        return 'Saved. Leads already resting keep their old rest until you apply the ladder below.';
      }),
    );
  }

  function useDeployDefault() {
    run(() =>
      withFreshRow(() => updateRedialPolicy({ ring_ladder: null })).then((r) => {
        onSaved(r);
        setRungs(r.redial.deploy_ring_ladder.map((s) => String(toHours(s))));
        setLadderDirty(false);
        return 'Back on the deploy-time ladder.';
      }),
    );
  }

  // Sends the PERSISTED ladder as the one to apply — the button is disabled while the boxes
  // hold unsaved edits, so that is also the ladder on screen — and the server refuses if the
  // stored one differs. The run is handed to the background and outlives this request, so
  // the answer here is "started", not "done".
  function restamp() {
    run(() =>
      restampRungs(baseline).then(
        () =>
          'Started. Every pool is being moved onto the ladder in force, in the background; leads whose new rest would be longer are left where they are. Each pool records what it moved on its own timeline.',
      ),
    );
  }

  return (
    <div className="mt-6 border-t pt-4">
      <h3 className="text-sm font-medium">Redial ladder</h3>
      <p className="text-muted-foreground text-xs">
        How long a lead rests after each ring, in hours. The last rung repeats forever. The first
        rung cannot go below one hour. In force now:{' '}
        <span className="tabular-nums">{redial.ring_ladder.map(toHours).join(' → ')}</span>
        {usingDefault && ' (the deploy default)'}.
      </p>

      <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
        {rungs.map((value, i) => (
          <label key={i} className="flex items-center justify-between gap-2 text-sm">
            <span className="text-muted-foreground">
              After {ordinal(i + 1)} ring{i === rungs.length - 1 ? ' and later' : ''}
            </span>
            <input
              type="number"
              min={i === 0 ? 1 : 0}
              step="0.5"
              value={value}
              disabled={busy}
              onChange={(e) => {
                setLadderDirty(true);
                setRungs(rungs.map((r, j) => (j === i ? e.target.value : r)));
              }}
              className="border-input bg-background h-8 w-24 rounded-md border px-2 text-right text-sm tabular-nums"
            />
          </label>
        ))}
      </div>

      <div className="mt-2 flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={busy || rungs.length >= 8}
          onClick={() => {
            setLadderDirty(true);
            setRungs([...rungs, rungs[rungs.length - 1] ?? '1']);
          }}
        >
          + Rung
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={busy || rungs.length <= 1}
          onClick={() => {
            setLadderDirty(true);
            setRungs(rungs.slice(0, -1));
          }}
        >
          − Rung
        </Button>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="flex items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground">
            Lifetime dials per lead
            <span className="block text-xs">
              Blank ={' '}
              {redial.deploy_max_dials_per_lead === null
                ? 'no cap'
                : `${redial.deploy_max_dials_per_lead}`}{' '}
              (the deploy default). In force: {redial.max_dials_per_lead ?? 'no cap'}.
            </span>
          </span>
          <input
            type="number"
            min={1}
            value={lifetimeCap}
            disabled={busy}
            onChange={(e) => setLifetimeCap(e.target.value)}
            className="border-input bg-background h-8 w-24 rounded-md border px-2 text-right text-sm tabular-nums"
          />
        </label>
        <label className="flex items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground">
            Release button skips leads at
            <span className="block text-xs">
              rings and above. Blank = {redial.deploy_ring_release_max_rings} (the deploy default).
              In force: {redial.ring_release_max_rings}.
            </span>
          </span>
          <input
            type="number"
            min={1}
            value={releaseCap}
            disabled={busy}
            onChange={(e) => setReleaseCap(e.target.value)}
            className="border-input bg-background h-8 w-24 rounded-md border px-2 text-right text-sm tabular-nums"
          />
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={busy} onClick={save}>
          {busy ? 'Working…' : 'Save'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={busy || usingDefault}
          onClick={useDeployDefault}
        >
          Use deploy default
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={busy || ladderDirty}
          title={ladderDirty ? 'Save the ladder first' : undefined}
          onClick={restamp}
        >
          Apply ladder to resting leads
        </Button>
      </div>
      <p className="text-muted-foreground mt-1 text-xs">
        Saving changes the rest for the next ring. Leads already resting keep the rest they were
        given until it lapses, so a shorter ladder reaches all of them within a week on its own.
        "Apply" moves them at once instead. It runs in the background across every pool, so it keeps
        going after this panel is closed, and each pool's timeline records what it moved.
      </p>

      {error && <p className="text-destructive mt-2 text-sm">{error}</p>}
      {message && <p className="mt-2 text-sm">{message}</p>}
    </div>
  );
}
